'use client'

import { AnimatePresence, m, useReducedMotion } from 'framer-motion'
import { useEffect, useId, useRef, useState } from 'react'
import { useLocale } from 'next-intl'
import { usePathname, useRouter } from '@/i18n/navigation'
import { routing } from '@/i18n/routing'

const ACCENT = 'var(--flagship-cyan, #22d3ee)'

// Native names: a visitor looks for their own language, not its German or English label.
const NATIVE_NAMES: Record<string, string> = {
  de: 'Deutsch',
  fi: 'Suomi',
  en: 'English',
  es: 'Español',
}

const GLASS_SHADOW =
  'inset 0 1px 0 0 rgba(255,255,255,0.35), inset 0 0 0 1px rgba(255,255,255,0.08), inset 0 0 24px -14px #22d3ee, 0 10px 30px -14px rgba(34,211,238,0.35)'

export default function LocaleSwitcher() {
  const locale = useLocale()
  const pathname = usePathname()
  const router = useRouter()
  const reduce = useReducedMotion()
  const [open, setOpen] = useState(false)
  // Highlight the chosen option first, then switch locale (the route change remounts this component).
  const [selected, setSelected] = useState(locale)
  const root = useRef<HTMLDivElement>(null)
  const trigger = useRef<HTMLButtonElement>(null)
  const options = useRef<Record<string, HTMLButtonElement | null>>({})
  const listId = useId()

  useEffect(() => {
    if (!open) return
    const onPointer = (e: PointerEvent) => {
      if (!root.current?.contains(e.target as Node)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false)
        trigger.current?.focus()
      }
    }
    document.addEventListener('pointerdown', onPointer)
    document.addEventListener('keydown', onKey)
    options.current[selected]?.focus()
    return () => {
      document.removeEventListener('pointerdown', onPointer)
      document.removeEventListener('keydown', onKey)
    }
  }, [open, selected])

  const select = (loc: string) => {
    if (loc === selected) {
      setOpen(false)
      return
    }
    setSelected(loc)
    setTimeout(() => router.replace(pathname, { locale: loc }), reduce ? 0 : 220)
  }

  const onListKey = (e: React.KeyboardEvent) => {
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return
    e.preventDefault()
    const locs = routing.locales as readonly string[]
    const current = locs.indexOf(document.activeElement?.getAttribute('data-locale') ?? selected)
    const next = (current + (e.key === 'ArrowDown' ? 1 : -1) + locs.length) % locs.length
    options.current[locs[next]]?.focus()
  }

  return (
    // Entry pop-in is a CSS animation (runs on the compositor from first paint, no JS frames).
    <div ref={root} className="locale-switcher-enter fixed top-4 right-4 z-[70]">
      <m.button
        ref={trigger}
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listId}
        whileHover={reduce ? undefined : { scale: 1.05 }}
        whileTap={reduce ? undefined : { scale: 0.95 }}
        className="group relative flex h-11 items-center gap-1.5 overflow-hidden rounded-full border border-white/25 bg-white/[0.04] pr-3 pl-3.5 text-xs font-bold text-[#f5f5ff] backdrop-blur-xl focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cyan-300"
        style={{ boxShadow: GLASS_SHADOW }}
      >
        <span className="pointer-events-none absolute inset-x-0 top-0 h-1/2 bg-gradient-to-b from-white/[0.12] to-transparent" />
        <svg aria-hidden viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="12" cy="12" r="9" />
          <path d="M3 12h18M12 3a14 14 0 0 1 0 18M12 3a14 14 0 0 0 0 18" />
        </svg>
        <span>{locale.toUpperCase()}</span>
        <span className="sr-only">{` – ${NATIVE_NAMES[locale] ?? locale}, language`}</span>
        <m.svg
          aria-hidden
          viewBox="0 0 24 24"
          className="h-3.5 w-3.5"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          animate={{ rotate: open ? 180 : 0 }}
          transition={reduce ? { duration: 0 } : { type: 'spring', stiffness: 400, damping: 25 }}
        >
          <path d="m6 9 6 6 6-6" />
        </m.svg>
      </m.button>

      <AnimatePresence>
        {open && (
          <m.div
            id={listId}
            role="listbox"
            aria-label="Language"
            onKeyDown={onListKey}
            initial={reduce ? { opacity: 0 } : { opacity: 0, y: -8, scale: 0.92 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={reduce ? { opacity: 0 } : { opacity: 0, y: -8, scale: 0.92 }}
            transition={reduce ? { duration: 0.1 } : { type: 'spring', stiffness: 420, damping: 30 }}
            className="absolute top-full right-0 mt-2 flex min-w-[10rem] origin-top-right flex-col gap-1 rounded-3xl border border-white/25 bg-[#0b0b14]/60 p-1.5 backdrop-blur-xl"
            style={{ boxShadow: GLASS_SHADOW }}
          >
            {routing.locales.map((loc, i) => {
              const active = loc === selected
              return (
                <m.button
                  key={loc}
                  ref={(el) => {
                    options.current[loc] = el
                  }}
                  type="button"
                  role="option"
                  aria-selected={active}
                  data-locale={loc}
                  onClick={() => select(loc)}
                  initial={reduce ? false : { opacity: 0, x: 8 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={reduce ? { duration: 0 } : { delay: 0.03 * i }}
                  className="relative flex min-h-11 items-center gap-3 rounded-full px-3 text-left text-sm transition-colors duration-200 hover:bg-white/10 focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-cyan-300"
                  style={{ color: active ? '#0b0b14' : '#f5f5ff' }}
                >
                  {active && (
                    <m.span
                      aria-hidden
                      initial={reduce ? false : { scale: 0.6, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      transition={reduce ? { duration: 0 } : { type: 'spring', stiffness: 420, damping: 30 }}
                      className="absolute inset-0 rounded-full"
                      style={{
                        backgroundColor: ACCENT,
                        boxShadow:
                          'inset 0 1px 0 0 rgba(255,255,255,0.6), inset 0 -2px 6px -2px rgba(0,0,0,0.25), 0 0 16px -2px #22d3ee',
                      }}
                    />
                  )}
                  <span className="relative w-6 text-xs font-bold">{loc.toUpperCase()}</span>
                  <span className="relative">{NATIVE_NAMES[loc] ?? loc}</span>
                </m.button>
              )
            })}
          </m.div>
        )}
      </AnimatePresence>
    </div>
  )
}
